import { useAuth } from '@/hooks/use-auth';
import { getCachedProfile, setCachedProfile, type Profile } from '@/lib/profile-cache';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export type { Profile };

/** Normalize thrown value to Error with a readable message (Supabase returns objects with .message). */
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return new Error((err as { message: string }).message);
  }
  return new Error(String(err));
}

/** Input for updating a profile (all optional) */
export type UpdateProfileInput = Partial<
  Pick<
    Profile,
    | 'display_name'
    | 'avatar_url'
    | 'dietary_preferences'
    | 'allergies'
    | 'default_servings'
    | 'measurement_system'
    | 'onboarding_completed'
  >
>;

/**
 * Fetch the current user's profile.
 * Uses in-memory cache so profile is not refetched on every screen that uses it.
 */
export function useProfile() {
  const { user } = useAuth();
  const cached = user?.id ? getCachedProfile(user.id) : null;
  const [profile, setProfile] = useState<Profile | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const existing = getCachedProfile(user.id);
    if (existing) {
      setProfile(existing);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;
      if (data) {
        const p = data as Profile;
        setProfile(p);
        setCachedProfile(p);
      } else {
        setProfile(null);
      }
    } catch (err) {
      const e = toError(err);
      setError(e);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useLayoutEffect(() => {
    if (!user?.id) {
      setProfile(null);
      setLoading(false);
      return;
    }
    const existing = getCachedProfile(user.id);
    if (existing) {
      setProfile(existing);
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (getCachedProfile(user.id)) return;
    fetchProfile();
  }, [user?.id, fetchProfile]);

  const updateProfile = useCallback(
    async (input: UpdateProfileInput): Promise<Profile | null> => {
      if (!user?.id) {
        setError(new Error('You must be signed in to update your profile'));
        return null;
      }

      setError(null);
      try {
        const { data, error: updateError } = await supabase
          .from('profiles')
          .update({
            ...input,
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id)
          .select()
          .single();

        if (updateError) throw updateError;
        if (data) {
          const p = data as Profile;
          setProfile(p);
          setCachedProfile(p);
          return p;
        }
        return null;
      } catch (err) {
        const e = toError(err);
        setError(e);
        throw e;
      }
    },
    [user?.id]
  );

  const displayProfile = user?.id ? (getCachedProfile(user.id) ?? profile) : null;
  const displayLoading = displayProfile ? false : loading;

  return { profile: displayProfile, loading: displayLoading, error, refreshProfile: fetchProfile, updateProfile };
}
